import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtCFA, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Eye, Printer } from "lucide-react";
import { PAIEMENTS } from "@/pages/Caisse";
import VendeurFilter from "@/components/VendeurFilter";
import { imprimerHTML, esc } from "@/lib/print";

const fmtJour = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
const LBL = { especes: "Espèces", wave: "Wave", orange_money: "Orange Money" };

function imprimerListe(items) {
  const sum = (k) => items.reduce((s, c) => s + (c[k] || 0), 0);
  const rows = items.map((c) => `<tr><td style="text-transform:capitalize">${esc(fmtJour(c.date))}</td><td class="num">${fmtCFA(c.montant_ouverture)}</td><td class="num">${fmtCFA(c.total_especes)}</td><td class="num">${fmtCFA(c.total_wave)}</td><td class="num">${fmtCFA(c.total_orange_money)}</td><td class="num">${c.nb_ventes}</td><td class="num">${fmtCFA(c.montant_fermeture)}</td></tr>`).join("");
  const total = `<tr class="total"><td>Total (${items.length} caisse${items.length > 1 ? "s" : ""})</td><td></td><td class="num">${fmtCFA(sum("total_especes"))}</td><td class="num">${fmtCFA(sum("total_wave"))}</td><td class="num">${fmtCFA(sum("total_orange_money"))}</td><td class="num">${sum("nb_ventes")}</td><td></td></tr>`;
  imprimerHTML("Historique des caisses", `<h1>LAC ROSE FITNESS</h1><h2>Historique des caisses clôturées</h2>
    <table><thead><tr><th>Date</th><th class="num">Fond</th><th class="num">Espèces</th><th class="num">Wave</th><th class="num">Orange Money</th><th class="num">Ventes</th><th class="num">Total en caisse</th></tr></thead><tbody>${rows}${total}</tbody></table>`);
}

function imprimerDetail(detail) {
  const { caisse: c, resume: r, ventes } = detail;
  const cards = [["Fond", c.montant_ouverture], ["Espèces", r.total_especes], ["Wave", r.total_wave], ["Orange Money", r.total_orange_money], ["Total ventes", r.total]]
    .map(([l, v]) => `<div class="card"><p class="l">${l}</p><p class="v">${fmtCFA(v)}</p></div>`).join("");
  const rows = ventes.map((v) => `<tr><td>${esc(fmtDate(v.created_at).slice(-5))}</td><td>${esc(v.items.map((i) => `${i.quantite}× ${i.nom}`).join(", "))}</td><td>${esc(v.vendeur_nom || "")}</td><td>${LBL[v.paiement] || esc(v.paiement)}${v.reference ? `<br><small>Réf. ${esc(v.reference)}</small>` : ""}</td><td class="num">${fmtCFA(v.total)}</td></tr>`).join("");
  imprimerHTML(`Caisse du ${c.date}`, `<h1>LAC ROSE FITNESS</h1><h2>Caisse du ${esc(fmtJour(c.date))}</h2>
    <p class="meta">Ouverte par ${esc(c.ouverte_par)} · Fermée par ${esc(c.fermee_par)} le ${esc(fmtDate(c.fermee_le))} · ${ventes.length} vente(s)</p>
    <div class="cards">${cards}</div>
    <table><thead><tr><th>Heure</th><th>Articles</th><th>Vendeur</th><th>Paiement</th><th class="num">Total</th></tr></thead><tbody>${rows}<tr class="total"><td colspan="4">Total</td><td class="num">${fmtCFA(r.total)}</td></tr></tbody></table>`);
}

export default function Historique() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [vendeur, setVendeur] = useState("");
  const params = vendeur ? { vendeur_id: vendeur } : {};

  useEffect(() => {
    setItems(null);
    api.get("/caisse/historique", { params: vendeur ? { vendeur_id: vendeur } : {} }).then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [vendeur]);

  const voir = async (c) => {
    try {
      const r = await api.get(`/caisse/${c.id}/ventes`, { params });
      setDetail(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const imprimer = async (c) => {
    try {
      const r = await api.get(`/caisse/${c.id}/ventes`, { params });
      imprimerDetail(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exporter = async (c) => {
    try {
      const r = await api.get(`/caisse/${c.id}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `caisse_${c.date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="historique-loading">Chargement…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="historique-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Historique des caisses</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length > 0 && items[0].mes_ventes ? (vendeur ? "Caisses et ventes du caissier sélectionné" : "Vos caisses et vos ventes uniquement") : "Caisses clôturées, détail des ventes et export CSV"}</p>
        </div>
        <div className="flex items-end gap-2">
          <VendeurFilter value={vendeur} onChange={setVendeur} testid="historique-vendeur-select" />
          <Button variant="outline" disabled={items.length === 0} onClick={() => imprimerListe(items)} className="border-border text-foreground/80" data-testid="historique-print-button">
            <Printer className="w-4 h-4 mr-2" /> Imprimer
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">Fond</TableHead>
                <TableHead className="text-muted-foreground">Espèces</TableHead>
                <TableHead className="text-muted-foreground">Wave</TableHead>
                <TableHead className="text-muted-foreground">Orange Money</TableHead>
                <TableHead className="text-muted-foreground">Ventes</TableHead>
                <TableHead className="text-muted-foreground text-right">Total en caisse</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="historique-empty">Aucune caisse clôturée</TableCell>
                </TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} className="border-border" data-testid={`caisse-row-${c.id}`}>
                  <TableCell className="font-medium text-foreground capitalize">{fmtJour(c.date)}</TableCell>
                  <TableCell className="text-foreground/80">{fmtCFA(c.montant_ouverture)}</TableCell>
                  <TableCell className="text-foreground/80">{fmtCFA(c.total_especes)}</TableCell>
                  <TableCell className="text-foreground/80">{fmtCFA(c.total_wave)}</TableCell>
                  <TableCell className="text-foreground/80">{fmtCFA(c.total_orange_money)}</TableCell>
                  <TableCell><Badge variant="outline" className="border-border text-muted-foreground">{c.nb_ventes}</Badge></TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600 dark:text-emerald-400">
                    {fmtCFA(c.montant_fermeture)}
                    {c.mes_ventes && <span className="block text-[11px] font-normal text-muted-foreground" data-testid={`mes-ventes-${c.id}`}>Mes ventes : {fmtCFA(c.mes_ventes.total)} ({c.mes_ventes.nb_ventes})</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600" data-testid={`voir-caisse-${c.id}`} onClick={() => voir(c)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600" data-testid={`print-caisse-${c.id}`} onClick={() => imprimer(c)}>
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600" data-testid={`export-caisse-${c.id}`} onClick={() => exporter(c)}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-card border-border text-foreground max-w-3xl" data-testid="caisse-detail-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Caisse du {detail && fmtJour(detail.caisse.date)}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {[
                  ["Fond", detail.caisse.montant_ouverture], ["Espèces", detail.resume.total_especes],
                  ["Wave", detail.resume.total_wave], ["Orange Money", detail.resume.total_orange_money],
                ].map(([l, v]) => (
                  <div key={l} className="bg-background border border-border rounded-lg p-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{l}</p>
                    <p className="font-bold text-foreground mt-1">{fmtCFA(v)}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Ouverte par {detail.caisse.ouverte_par} · Fermée par {detail.caisse.fermee_par} le {fmtDate(detail.caisse.fermee_le)}
              </p>
              <div className="max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">Heure</TableHead>
                      <TableHead className="text-muted-foreground">Articles</TableHead>
                      <TableHead className="text-muted-foreground">Paiement</TableHead>
                      <TableHead className="text-muted-foreground text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.ventes.map((v) => (
                      <TableRow key={v.id} className="border-border" data-testid={`detail-vente-${v.id}`}>
                        <TableCell className="text-sm text-foreground/80">{fmtDate(v.created_at).slice(-5)}</TableCell>
                        <TableCell className="text-sm text-foreground/80">{v.items.map((i) => `${i.quantite}× ${i.nom}`).join(", ")}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={PAIEMENTS[v.paiement]?.badge}>{PAIEMENTS[v.paiement]?.label || v.paiement}</Badge>
                          {v.reference && <span className="block text-[11px] text-muted-foreground mt-1">Réf. {v.reference}</span>}
                        </TableCell>
                        <TableCell className="text-right text-sm font-semibold text-emerald-600 dark:text-emerald-400">{fmtCFA(v.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => imprimerDetail(detail)} className="border-border text-foreground/80" data-testid="detail-print-button">
                  <Printer className="w-4 h-4 mr-2" /> Imprimer
                </Button>
                <Button onClick={() => exporter(detail.caisse)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="detail-export-button">
                  <Download className="w-4 h-4 mr-2" /> Exporter en CSV
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
